import { apiOrchestrator } from './apiService';
import { RefreshResponse } from '../types/authTypes';
import { CookieManager } from '../utils/cookieManager';
import { TokenUtils } from '../utils/tokenUtils';

export interface RefreshTokenOptions {
  maxRetries?: number;
  retryDelay?: number; // em milissegundos
  forceRefresh?: boolean; // forçar refresh mesmo se token ainda válido
}

export class RefreshTokenService {
  private static isRefreshing = false;
  private static failedQueue: Array<{
    resolve: (value: string) => void;
    reject: (reason: any) => void;
  }> = [];
  private static retryCount = 0;
  private static maxRetries = 3;
  private static retryDelay = 1000; // 1 segundo

  /**
   * Verifica se o usuário está logado (tem access_token)
   * O refresh_token HttpOnly será enviado automaticamente pelo navegador
   */
  static checkRefreshTokenAvailability(): boolean {
    const cookies = document.cookie.split(';').map(c => c.trim());
    const accessTokenCookie = cookies.find(c => c.startsWith('access_token='));

    console.log('[RefreshTokenService] 🔍 Verificando se usuário está logado:', {
      hasAccessToken: !!accessTokenCookie,
      note: 'refresh_token HttpOnly será enviado automaticamente pelo navegador',
    });

    // Se temos access_token, assumimos que o refresh_token também está disponível
    return !!accessTokenCookie;
  }

  /**
   * Renova o access token usando o refresh token
   */
  static async refreshAccessToken(options: RefreshTokenOptions = {}): Promise<string> {
    const {
      maxRetries = this.maxRetries,
      retryDelay = this.retryDelay,
      forceRefresh = false,
    } = options;

    console.log('[RefreshTokenService] 🚀 Iniciando refresh do token...', {
      forceRefresh,
      maxRetries,
      retryDelay,
      timestamp: new Date().toISOString(),
      isRefreshing: this.isRefreshing,
      queueLength: this.failedQueue.length,
    });

    // Verificar se o refresh token está disponível
    const hasRefreshToken = this.checkRefreshTokenAvailability();
    if (!hasRefreshToken) {
      console.error('[RefreshTokenService] ❌ Refresh token não encontrado nos cookies');
      throw new Error('Refresh token não encontrado nos cookies');
    }

    // Verificar se já está fazendo refresh
    if (this.isRefreshing && !forceRefresh) {
      console.log('[RefreshTokenService] ⏳ Refresh já em andamento, adicionando à fila...', {
        queueLength: this.failedQueue.length,
        timestamp: new Date().toISOString(),
      });
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    // Verificar se o token atual ainda é válido (se não for force refresh)
    if (!forceRefresh) {
      const currentToken = CookieManager.getAccessToken();
      if (currentToken && !TokenUtils.isTokenNearExpiry(currentToken, 60)) {
        console.log('[RefreshTokenService] Token ainda válido, não é necessário refresh');
        return currentToken;
      }
    }

    this.isRefreshing = true;
    this.retryCount = 0;

    try {
      console.log('[RefreshTokenService] Iniciando refresh do token...');

      const newToken = await this.performRefresh(maxRetries, retryDelay);

      // Salvar novo token e expiração
      this.saveNewToken(newToken);

      // Processar fila de requisições pendentes
      this.processQueue(null, newToken);

      console.log('[RefreshTokenService] Token renovado com sucesso');
      return newToken;
    } catch (error) {
      console.error('[RefreshTokenService] ❌ Falha crítica após todas as tentativas:', error);

      // Processar fila com erro
      this.processQueue(error, null);

      // Limpeza total de autenticação
      await this.performCompleteAuthCleanup(error);

      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Executa o refresh com retry automático
   */
  private static async performRefresh(maxRetries: number, retryDelay: number): Promise<string> {
    console.log('[RefreshTokenService] 🔄 Iniciando processo de refresh com retry...', {
      maxRetries,
      retryDelay,
      timestamp: new Date().toISOString(),
    });

    while (this.retryCount <= maxRetries) {
      try {
        console.log(`[RefreshTokenService] 🔄 Tentativa ${this.retryCount + 1} de refresh...`, {
          retryCount: this.retryCount,
          maxRetries,
          usingHttpOnlyCookie: true,
          timestamp: new Date().toISOString(),
        });

        // ESTRATÉGIA: Usar apenas cookie HttpOnly (backend está funcionando perfeitamente)
        const baseURL = apiOrchestrator.getInstance().defaults.baseURL || '';
        const refreshUrl = `${baseURL}/auth/refresh-token`;

        console.log(
          '[RefreshTokenService] 🔄 Iniciando refresh - cookies HttpOnly serão enviados automaticamente:',
          {
            url: refreshUrl,
            withCredentials: true,
            timestamp: new Date().toISOString(),
          }
        );

        // Usar a instância do orchestrator que já tem withCredentials configurado
        const response = await apiOrchestrator.post<RefreshResponse>(
          '/auth/refresh-token',
          undefined,
          {
            headers: {},
          }
        );

        console.log('[RefreshTokenService] 📡 Resposta da requisição:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          data: response.data,
        });

        console.log('[RefreshTokenService] ✅ Resposta recebida do servidor:', {
          status: response.status,
          hasAccessToken: !!response.data.access_token,
          accessTokenLength: response.data.access_token?.length || 0,
          accessTokenStart: response.data.access_token?.substring(0, 20) + '...' || 'N/A',
          hasExpiresIn: !!response.data.expires_in,
          expiresIn: response.data.expires_in,
          hasNewRefreshToken: !!response.data.refresh_token,
          newRefreshTokenLength: response.data.refresh_token?.length || 0,
          timestamp: new Date().toISOString(),
        });

        const {
          status,
          access_token: accessTokenFromBody,
          token: tokenAlias,
          expires_in,
          refresh_token: newRefreshToken,
        } = response.data || {};

        // Checar header X-New-Access-Token (exposto pelo backend)
        const headerToken = (response.headers as any)?.['x-new-access-token'] as string | undefined;

        // Alguns backends retornam token no cookie HttpOnly + status no body.
        // Vamos aceitar os dois formatos: body ou cookie (fallback lendo do CookieManager)
        let access_token = accessTokenFromBody || tokenAlias || headerToken;

        if (!access_token) {
          // Fallback: tentar ler access_token do cookie (se o backend estiver setando)
          const tokenFromCookie = CookieManager.getAccessToken();
          if (tokenFromCookie) {
            console.log('[RefreshTokenService] 🔑 Token obtido do cookie de access_token');
            access_token = tokenFromCookie;
          }
        }

        if (status && status !== 'success') {
          console.error('[RefreshTokenService] ❌ Refresh retornou status não-sucesso:', status);
          throw new Error(`Refresh denied: ${status}`);
        }

        if (!access_token) {
          console.error(
            '[RefreshTokenService] ❌ Token de acesso não recebido na resposta (body ou cookie)'
          );
          throw new Error('Token de acesso não recebido na resposta');
        }

        // Se o backend retornou um novo refresh token, salvá-lo
        if (newRefreshToken) {
          console.log('[RefreshTokenService] 🔄 Novo refresh token recebido, atualizando...', {
            newRefreshTokenLength: newRefreshToken.length,
            timestamp: new Date().toISOString(),
          });
          CookieManager.setRefreshToken(newRefreshToken);
        }

        console.log('[RefreshTokenService] 🎯 Refresh concluído com sucesso!', {
          accessTokenLength: access_token.length,
          expiresIn: expires_in,
          timestamp: new Date().toISOString(),
        });

        return access_token;
      } catch (error: any) {
        this.retryCount++;

        if (this.retryCount > maxRetries) {
          console.error(`[RefreshTokenService] Máximo de tentativas (${maxRetries}) atingido`);
          throw error;
        }

        console.warn(
          `[RefreshTokenService] Tentativa ${this.retryCount} falhou, tentando novamente em ${retryDelay}ms...`,
          error.message
        );

        // Aguardar antes da próxima tentativa
        await this.delay(retryDelay);

        // Aumentar delay exponencialmente
        retryDelay *= 2;
      }
    }

    throw new Error('Erro inesperado durante refresh do token');
  }

  /**
   * Salva o novo token e sua expiração
   */
  private static saveNewToken(token: string): void {
    try {
      // Salvar access token
      CookieManager.setAccessToken(token);

      // Calcular e salvar expiração
      const validation = TokenUtils.validateToken(token);
      if (validation.expiresAt) {
        CookieManager.setTokenExpiry(validation.expiresAt);
        console.log(
          `[RefreshTokenService] Token salvo com expiração em: ${new Date(validation.expiresAt)}`
        );
      } else {
        // Se não conseguir extrair expiração, definir como 1 hora
        const defaultExpiry = TokenUtils.generateExpiryTimestamp(3600);
        CookieManager.setTokenExpiry(defaultExpiry);
        console.log(
          `[RefreshTokenService] Expiração padrão definida para: ${new Date(defaultExpiry)}`
        );
      }
    } catch (error) {
      console.error('[RefreshTokenService] Erro ao salvar novo token:', error);
    }
  }

  /**
   * Processa a fila de requisições pendentes
   */
  private static processQueue(error: any, token: string | null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token!);
      }
    });

    this.failedQueue = [];
  }

  /**
   * Verifica se o token atual precisa de refresh
   */
  static needsRefresh(thresholdSeconds: number = 300): boolean {
    const currentToken = CookieManager.getAccessToken();

    if (!currentToken) {
      return true;
    }

    return TokenUtils.isTokenNearExpiry(currentToken, thresholdSeconds);
  }

  /**
   * Obtém informações sobre o estado do refresh
   */
  static getStatus(): {
    isRefreshing: boolean;
    retryCount: number;
    queueLength: number;
    lastRefreshAttempt?: Date;
  } {
    return {
      isRefreshing: this.isRefreshing,
      retryCount: this.retryCount,
      queueLength: this.failedQueue.length,
    };
  }

  /**
   * Limpa o estado do serviço
   */
  static clearState(): void {
    this.isRefreshing = false;
    this.failedQueue = [];
    this.retryCount = 0;
    console.log('[RefreshTokenService] Estado limpo');
  }

  /**
   * Força um refresh imediato
   */
  static async forceRefresh(): Promise<string> {
    console.log('[RefreshTokenService] Forçando refresh imediato...');
    return this.refreshAccessToken({ forceRefresh: true });
  }

  /**
   * Delay utilitário
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Verifica se o serviço está funcionando
   */
  static async healthCheck(): Promise<boolean> {
    console.warn(
      '[RefreshTokenService] healthCheck() chamado, mas endpoint não existe. Retornando false.'
    );
    return false;
  }

  /**
   * Executa limpeza completa de autenticação após falha crítica
   */
  private static async performCompleteAuthCleanup(error: any): Promise<void> {
    try {
      console.log('[RefreshTokenService] 🧹 Iniciando limpeza completa de autenticação...', {
        error: error?.message || 'Erro desconhecido',
        timestamp: new Date().toISOString(),
      });

      // 1. Limpar todos os cookies de autenticação
      CookieManager.clearAllAuthCookies();
      console.log('[RefreshTokenService] ✅ Cookies de autenticação limpos');

      // 2. Limpar localStorage completamente
      const authKeys = [
        'AUTH_TOKEN',
        'user_id',
        'userData',
        'x-cognito-userpool',
        'x-organization-uuid',
        'menuData',
        'selectedMenus',
        'menuTitle',
        'lastSelectedMenu',
      ];

      authKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('[RefreshTokenService] ✅ LocalStorage limpo');

      // 3. Limpar sessionStorage (se houver dados de auth)
      const sessionAuthKeys = ['AUTH_TOKEN', 'user_id', 'userData', 'temp_auth_data'];

      sessionAuthKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
      console.log('[RefreshTokenService] ✅ SessionStorage limpo');

      // 4. Limpar headers das APIs
      try {
        const { clearHeaders } = await import('../utils/headersManager');
        clearHeaders();
        console.log('[RefreshTokenService] ✅ Headers das APIs limpos');
      } catch (importError) {
        console.warn('[RefreshTokenService] ⚠️ Não foi possível limpar headers:', importError);
      }

      // 5. Emitir evento de logout forçado
      const logoutEvent = new CustomEvent('forceLogout', {
        detail: {
          reason: 'refresh_token_failed',
          error: error?.message || 'Erro desconhecido',
          timestamp: new Date().toISOString(),
        },
      });

      window.dispatchEvent(logoutEvent);
      console.log('[RefreshTokenService] ✅ Evento forceLogout emitido');

      // 6. Log final
      console.log('[RefreshTokenService] 🎯 Limpeza completa de autenticação finalizada');
    } catch (cleanupError) {
      console.error('[RefreshTokenService] ❌ Erro durante limpeza de autenticação:', cleanupError);

      // Mesmo com erro na limpeza, emitir evento de logout
      window.dispatchEvent(
        new CustomEvent('forceLogout', {
          detail: {
            reason: 'cleanup_error',
            error: cleanupError instanceof Error ? cleanupError.message : 'Erro na limpeza',
            timestamp: new Date().toISOString(),
          },
        })
      );
    }
  }
}
